import { useCallback, useEffect, useRef } from "react";

/** The two signer columns a scroll-spy section can belong to. */
export type ScrollSpyApplicant = "driver" | "secondSigner";

interface SectionMeta<T extends string> {
  category: T;
  /** Undefined for sections that don't belong to a signer column (e.g. Vehicle). */
  applicant: ScrollSpyApplicant | undefined;
  isIntersecting: boolean;
}

/**
 * A ref callback factory: `registerSection("Income", "driver")` returns a
 * callback ref to attach to that section's DOM node. `applicant` is omitted
 * for sections outside the signer columns (e.g. `registerSection("Vehicle")`).
 * Multiple elements can share the same `(category, applicant)` pair (e.g. one per income block) —
 * the factory caches and returns the SAME callback for a given pair, so
 * passing it inline as `ref={...}` never churns the ref across unrelated
 * re-renders (e.g. a sibling field's keystroke).
 */
export type SectionRegistrar<T extends string> = (
  category: T,
  applicant?: ScrollSpyApplicant
) => (element: HTMLElement | null) => (() => void) | void;

// Narrow horizontal band roughly a third of the way down the viewport — a
// section only counts as "active" once it has scrolled up into this band,
// matching typical scrollspy behavior (the heading you're "at" is the one
// just below this line, not the one merely peeking into view at the top).
const ACTIVATION_ROOT_MARGIN = "-30% 0px -55% 0px";

/** The active category, plus which applicant(s)' section under it are currently intersecting — both, when the two signer columns are aligned side by side on the same section; empty for a section outside the signer columns. */
export interface ScrollSpyActive<T extends string> {
  category: T;
  applicants: ScrollSpyApplicant[];
}

/**
 * A stable, order-independent key for an active report — shared by `reportIfChanged` and `resyncActiveSection` so both dedupe/compare the exact same way.
 * @param {ScrollSpyActive<T> | undefined} active - The active report, if any.
 * @returns {string | undefined} The signature, or undefined when nothing is active.
 */
function activeSignature<T extends string>(active: ScrollSpyActive<T> | undefined): string | undefined {
  return active ? `${active.category}:${[...active.applicants].sort().join(",")}` : undefined;
}

export interface ScrollSpyControls<T extends string> {
  /** A ref-callback factory to attach to each candidate section element. */
  registerSection: SectionRegistrar<T>;
  /**
   * Re-derives and re-reports the currently active section from the
   * IntersectionObserver's live (already-tracked) state, bypassing the
   * "only when it actually changes" dedupe. Needed because a consumer may
   * have deliberately discarded a report without applying it (e.g. the
   * Stipulations category filter ignores scroll-spy updates while pinned to
   * "All Stips" at the very top of the page) — if the section that was
   * discarded is still the active one once that pin is released, the
   * underlying `IntersectionObserver` won't fire again on its own (nothing
   * actually toggled), so nothing would ever re-apply it without this.
   */
  resyncActiveSection: () => void;
}

/**
 * Tracks a set of registered section elements — each tagged with a category
 * and which signer column (applicant) it belongs to — and reports whichever
 * category is most relevant within the activation band as the user scrolls,
 * together with which applicant(s)' section under that category are
 * currently intersecting. Two columns' matching sections (e.g. Driver's and
 * 2nd Signer's Income) are row-aligned in the Signers grid, so when the
 * layout is side by side both intersect the band at once and both are
 * reported; when the layout is stacked (or only one signer is present) only
 * one ever does. Sections may mount and unmount freely (e.g. a collapsed
 * subsection) — `registerSection`'s cleanup function deregisters them.
 * @template T
 * @param categoryOrder - Every trackable category, top to bottom in page
 *   order. When two categories straddle the activation band at once, the one
 *   later in this list wins. A fixed order (rather than registration order)
 *   keeps that tie-break correct for sections that mount late, e.g. once
 *   permissions resolve. A category missing from this list never wins.
 * @param onActiveChange - Called with the active section, only when it actually changes.
 *   Called with `undefined` when the analyst scrolls back above the first
 *   section (nothing is intersecting the activation band) — e.g. so a caller
 *   can revert to an "all sections" fallback.
 * @returns {ScrollSpyControls<T>} `registerSection` (a ref-callback factory) and `resyncActiveSection` (see above).
 */
export function useScrollSpy<T extends string>(
  categoryOrder: readonly T[],
  onActiveChange: (active: ScrollSpyActive<T> | undefined) => void
): ScrollSpyControls<T> {
  const sectionsReference = useRef(new Map<Element, SectionMeta<T>>());
  // One stable ref-callback per distinct `(category, applicant)` pair, reused
  // across every element registered under it (e.g. every income block in
  // that column) and across every re-render — so passing
  // `registerSection("Income", "driver")` inline as a JSX `ref` never
  // triggers a spurious detach/reattach on an unrelated re-render.
  const registrarsReference = useRef(new Map<string, (element: HTMLElement | null) => (() => void) | void>());
  // Ranked per CATEGORY only (not per applicant) — the tie-break must hold
  // regardless of which signer column's section is the one intersecting.
  const categoryOrderReference = useRef(categoryOrder);
  categoryOrderReference.current = categoryOrder;
  // `undefined` doubles as "nothing intersecting yet" and "nothing intersecting
  // anymore" (e.g. scrolled back above the first section) — both cases should
  // report the same "no active section" state to the caller.
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef requires an explicit initial value
  const lastActiveSignatureReference = useRef<string | undefined>(undefined);
  const onActiveChangeReference = useRef(onActiveChange);
  onActiveChangeReference.current = onActiveChange;
  const observerReference = useRef<IntersectionObserver | null>(null);

  useEffect(() => () => observerReference.current?.disconnect(), []);

  // Shared between the IntersectionObserver callback and `resyncActiveSection`
  // so both use the exact same "which section wins" tie-break rule. Two
  // plain-array passes over the (at most a handful of) registered sections —
  // deliberately no Map/Set allocation here, since this can run on every
  // scroll-driven IntersectionObserver tick and the domain is bounded to a
  // handful of page sections (each signer-column category at most twice).
  const computeActive = useCallback((): ScrollSpyActive<T> | undefined => {
    let winningCategory: T | undefined;
    let winningOrder = -1;

    for (const meta of sectionsReference.current.values()) {
      if (!meta.isIntersecting) {
        continue;
      }
      const order = categoryOrderReference.current.indexOf(meta.category);
      if (order > winningOrder) {
        winningOrder = order;
        winningCategory = meta.category;
      }
    }

    if (winningCategory === undefined) {
      return undefined;
    }

    const applicants: ScrollSpyApplicant[] = [];
    for (const meta of sectionsReference.current.values()) {
      if (
        meta.isIntersecting &&
        meta.category === winningCategory &&
        meta.applicant !== undefined &&
        !applicants.includes(meta.applicant)
      ) {
        applicants.push(meta.applicant);
      }
    }

    return { category: winningCategory, applicants };
  }, []);

  const reportIfChanged = useCallback((active: ScrollSpyActive<T> | undefined) => {
    const signature = activeSignature(active);
    if (signature !== lastActiveSignatureReference.current) {
      lastActiveSignatureReference.current = signature;
      onActiveChangeReference.current(active);
    }
  }, []);

  const resyncActiveSection = useCallback(() => {
    // Deliberately bypasses `reportIfChanged`'s dedupe: the caller may have
    // discarded an earlier report of this exact same value (e.g. the
    // Stipulations filter ignores scroll-spy updates while pinned to "All
    // Stips"), so `lastActiveSignatureReference` can already equal the
    // current active value even though the consumer never actually applied
    // it. Always re-deliver it here so the consumer's state gets back in sync.
    const active = computeActive();
    lastActiveSignatureReference.current = activeSignature(active);
    onActiveChangeReference.current(active);
  }, [computeActive]);

  const registerSection = useCallback<SectionRegistrar<T>>(
    (category: T, applicant?: ScrollSpyApplicant) => {
      const key = `${applicant ?? ""}:${category}`;
      const cached = registrarsReference.current.get(key);
      if (cached) {
        return cached;
      }

      const registrar = (element: HTMLElement | null) => {
        if (!element) {
          return;
        }

        // Created lazily on the first section's actual DOM commit (here, inside
        // the ref callback) rather than during render, so a render that never
        // commits (e.g. an interrupted concurrent render) never allocates one.
        if (observerReference.current === null && typeof IntersectionObserver !== "undefined") {
          observerReference.current = new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                const meta = sectionsReference.current.get(entry.target);
                if (!meta) {
                  continue;
                }
                meta.isIntersecting = entry.isIntersecting;
              }
              reportIfChanged(computeActive());
            },
            { rootMargin: ACTIVATION_ROOT_MARGIN }
          );
        }

        sectionsReference.current.set(element, { category, applicant, isIntersecting: false });
        observerReference.current?.observe(element);
        return () => {
          sectionsReference.current.delete(element);
          observerReference.current?.unobserve(element);
        };
      };

      registrarsReference.current.set(key, registrar);
      return registrar;
    },
    [computeActive, reportIfChanged]
  );

  return { registerSection, resyncActiveSection };
}
